import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    TextField,
    Button,
    Stack,
    Divider,
    InputAdornment,
    Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import {
    searchStockName,
    getRecentlyViewed,
    getStockByTicker,
} from "../../../api/api";
import { addHoldingToPortfolio } from "../../../api/portfolio_api";
import { fmtNumber } from "./portfolioHelpers";
import { getCurrencySymbol } from "../../../utils/currency"; // ← adjust path

const today = new Date().toISOString().split("T")[0];
const LIST_MAX_HEIGHT = 240;

// const fmtNumber = (v, d = 2) =>
//     v === null || v === undefined || isNaN(v)
//         ? "—"
//         : Number(v).toLocaleString(undefined, {
//             minimumFractionDigits: d,
//             maximumFractionDigits: d,
//         });

const normalizeStock = (d) => ({
    ticker: d.ticker,
    company_name: d.company_name,
    price: Number(d.current_price || 0),
});

const inputSx = (accent = "var(--color-primary)") => ({
    fontSize: "var(--font-size-sm)",
    borderRadius: 1,
    bgcolor: "var(--color-bg-secondary)",
    color: "var(--color-text-primary)",
    "& fieldset": { borderColor: "var(--color-border-primary)" },
    "&:hover fieldset": { borderColor: "var(--color-border-secondary)" },
    "&.Mui-focused fieldset": { borderColor: accent, borderWidth: "1px" },
});

const AddHoldingsDialog = ({
    open,
    onClose,
    portfolioId,
    existingHoldings = [],
    onSaved,
}) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [recent, setRecent] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    const [selected, setSelected] = useState(null);
    // ── shares stored as STRING so the field can be fully cleared ──
    const [shares, setShares] = useState("");
    const [sharesError, setSharesError] = useState(false);
    const [manualPrice, setManualPrice] = useState("");
    const [purchaseDate, setPurchaseDate] = useState(today);

    const [pending, setPending] = useState([]);
    const [saving, setSaving] = useState(false);
    const [pendingSharesErrors, setPendingSharesErrors] = useState({});

    const sym = getCurrencySymbol();

    const existingTickers = useMemo(
        () => new Set(existingHoldings.map((h) => h.ticker)),
        [existingHoldings]
    );

    const resetForm = () => {
        setShares("");
        setSharesError(false);
        setManualPrice("");
        setPurchaseDate(today);
    };

    useEffect(() => {
        if (!open) {
            setQuery("");
            setResults([]);
            setSelected(null);
            resetForm();
            return;
        }

        const loadRecent = async () => {
            const res = await getRecentlyViewed();
            if (res?.success) {
                const map = {};
                res.data.forEach((i) => (map[i.ticker] = normalizeStock(i)));
                setRecent(Object.values(map).slice(0, 3));
            }
        };

        loadRecent();
    }, [open]);

    useEffect(() => {
        if (!query.trim()) { setResults([]); return; }

        let active = true;
        const fetch = async () => {
            setLoadingList(true);
            const res = await searchStockName(query);
            if (active && res?.success) setResults(res.data || []);
            setLoadingList(false);
        };

        const id = setTimeout(fetch, 300);
        return () => { active = false; clearTimeout(id); };
    }, [query]);

    const handleSelect = async (item) => {
        if (existingTickers.has(item.ticker)) return;

        const res = await getStockByTicker(item.ticker);
        if (res?.success) {
            const normalized = normalizeStock(res.data);
            setSelected(normalized);
            resetForm();
            setManualPrice(String(normalized.price));
        }
    };

    // ── shares change: allow empty or positive integers only ──
    const handleSharesChange = (e) => {
        const raw = e.target.value;
        // allow only digits (no decimals, no negatives)
        if (raw === "" || /^[1-9]\d*$/.test(raw)) {
            setShares(raw);
            setSharesError(false);
        }
    };

    const handleAddPending = () => {
        if (!selected) return;

        // validate: must be non-empty and >= 1
        if (shares === "" || Number(shares) < 1) {
            setSharesError(true);
            return;
        }

        setPending((p) => [
            ...p,
            {
                ...selected,
                shares: Number(shares),
                cost_basis: Number(manualPrice),
                purchase_date: purchaseDate,
            },
        ]);

        setSelected(null);
        setQuery("");
        resetForm();
    };

    const updatePendingShares = (idx, raw) => {
        // allow empty or positive integers only
        if (raw === "" || /^[1-9]\d*$/.test(raw)) {
            setPending((p) => p.map((item, i) => i === idx ? { ...item, shares: raw } : item));
            setPendingSharesErrors((e) => ({ ...e, [idx]: false }));
        }
    };

    const removePending = (idx) => {
        setPending((p) => p.filter((_, i) => i !== idx));
        setPendingSharesErrors((e) => {
            const next = { ...e };
            delete next[idx];
            return next;
        });
    };

    const handleSaveAll = async () => {
        // validate all pending shares before saving
        const errors = {};
        pending.forEach((p, idx) => {
            if (p.shares === "" || Number(p.shares) < 1) errors[idx] = true;
        });
        if (Object.keys(errors).length > 0) {
            setPendingSharesErrors(errors);
            return;
        }

        setSaving(true);

        for (const h of pending) {
            await addHoldingToPortfolio(portfolioId, {
                ticker: h.ticker,
                shares: h.shares,
                cost_basis: h.cost_basis * h.shares,
                purchase_date: h.purchase_date || today,
            });
        }

        setPending([]);
        setSaving(false);
        onSaved?.();
        onClose();
    };

    const parsedShares = Number(shares);
    const totalCost = shares !== "" && !isNaN(parsedShares)
        ? parsedShares * Number(manualPrice || 0)
        : 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: "var(--color-surface)",
                    backgroundImage: "none",
                    borderRadius: 1.5,
                    border: "1px solid var(--color-border-primary)",
                    boxShadow: "var(--shadow-xl)",
                    m: { xs: 2, sm: 3 },
                    maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 48px)" },
                    width: { xs: "calc(100% - 32px)", sm: "540px" },
                    maxWidth: { xs: "calc(100% - 32px)", sm: "540px" },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    pb: 1.25, pt: 1.75, px: 2.5,
                    fontSize: 16, fontWeight: 600,
                    color: "var(--color-text-primary)",
                    borderBottom: "1px solid var(--color-border-primary)",
                }}
            >
                Add holdings
                <IconButton onClick={onClose} size="small"
                    sx={{ color: "var(--color-text-muted)", p: 0.5, "&:hover": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-secondary)" } }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 2.5, pb: 2.5, pt: 2 }}>

                {/* ── SEARCH ── */}
                {!selected && (
                    <TextField
                        fullWidth size="small"
                        placeholder="Search by ticker or company name"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: "var(--color-text-muted)" }} />
                                </InputAdornment>
                            ),
                            sx: { ...inputSx(), mt: 2, "& input::placeholder": { color: "var(--color-text-muted)", opacity: 1 } },
                        }}
                        sx={{ mb: 1.5 }}
                    />
                )}

                {/* ── RESULTS LIST ── */}
                {!selected && (
                    <Box sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: "auto", mb: 1.5 }}>
                        {query && results.map((i) => (
                            <OptionRow key={i.ticker} item={i} disabled={existingTickers.has(i.ticker)} onSelect={handleSelect} />
                        ))}

                        {loadingList && (
                            <Box textAlign="center" py={2.5}>
                                <CircularProgress size={20} sx={{ color: "var(--color-primary)" }} />
                            </Box>
                        )}

                        {!query && !loadingList && results.length === 0 && recent.length > 0 && (
                            <Fade in timeout={300}>
                                <Box>
                                    <Box display="flex" alignItems="center" gap={0.5} mb={1.25} px={0.75}>
                                        <TrendingUpIcon sx={{ fontSize: 15, color: "var(--color-text-muted)" }} />
                                        <Typography fontSize="var(--font-size-xs2)" fontWeight={600} color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.04em">
                                            Recently viewed
                                        </Typography>
                                    </Box>
                                    {recent.map((i) => (
                                        <OptionRow key={i.ticker} item={i} disabled={existingTickers.has(i.ticker)} onSelect={handleSelect} />
                                    ))}
                                </Box>
                            </Fade>
                        )}

                        {query && !loadingList && results.length === 0 && (
                            <Box py={2.5} textAlign="center">
                                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">No results found</Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* ── SELECTED STOCK FORM ── */}
                {selected && (
                    <Fade in timeout={300}>
                        <Stack spacing={2} my={1.5}>
                            <Box sx={{ bgcolor: "var(--color-bg-secondary)", borderRadius: 1, p: 1.75, border: "1px solid var(--color-border-primary)" }}>
                                <Typography fontSize="var(--font-size-sm)" fontWeight={600} color="var(--color-text-primary)" mb={0.5}>
                                    {selected.ticker}
                                </Typography>
                                <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)" lineHeight={1.5}>
                                    {selected.company_name}
                                </Typography>

                                {/* Price per share */}
                                <Box mt={1.5} pt={1.5} borderTop="1px solid var(--color-border-primary)"
                                    display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">Price per quantity</Typography>
                                    <TextField
                                        type="number" size="small"
                                        value={manualPrice}
                                        onChange={(e) => setManualPrice(e.target.value)}
                                        inputProps={{ min: 0, step: "0.01" }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography sx={{ color: "var(--color-text-primary)", fontSize: "var(--font-size-sm)" }}>{sym}</Typography>
                                                </InputAdornment>
                                            ),
                                            sx: inputSx(),
                                        }}
                                        InputLabelProps={{ sx: { fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", "&.Mui-focused": { color: "var(--color-primary)" } } }}
                                    />
                                </Box>

                                {/* ── Number of shares ── */}
                                <Box mt={1.5} pt={1.5} borderTop="1px solid var(--color-border-primary)"
                                    display="flex" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">Number of quantity</Typography>
                                        {sharesError && (
                                            <Typography
                                                fontSize="var(--font-size-xs2)"
                                                color="var(--color-error)"
                                                mt={0.4}
                                                lineHeight={1.3}
                                            >
                                                Please enter a valid quantity
                                            </Typography>
                                        )}
                                    </Box>
                                    <TextField
                                        type="text"
                                        inputMode="numeric"
                                        size="small"
                                        placeholder="e.g. 10"
                                        value={shares}
                                        onChange={handleSharesChange}
                                        error={sharesError}
                                        inputProps={{ min: 1 }}
                                        InputProps={{
                                            sx: {
                                                ...inputSx(sharesError ? "var(--color-error)" : undefined),
                                                "& input::placeholder": { color: "var(--color-text-muted)", opacity: 0.3 },
                                            },
                                        }}
                                        InputLabelProps={{ sx: { fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", "&.Mui-focused": { color: "var(--color-primary)" } } }}
                                    />
                                </Box>

                                {/* Purchase date */}
                                <Box mt={1.5} pt={1.5} borderTop="1px solid var(--color-border-primary)"
                                    display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">Purchase date</Typography>
                                    <TextField
                                        type="date" size="small"
                                        value={purchaseDate}
                                        onChange={(e) => setPurchaseDate(e.target.value)}
                                        inputProps={{ max: today }}
                                        InputProps={{ sx: inputSx() }}
                                    />
                                </Box>
                            </Box>

                            {/* Total cost */}
                            <Box sx={{ bgcolor: "var(--color-bg-secondary)", borderRadius: 1, p: 1.25, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" fontWeight={500}>Total cost</Typography>
                                <Typography fontSize="var(--font-size-base)" fontWeight={600} color="var(--color-success)">
                                    {sym}{fmtNumber(totalCost, 2)}
                                </Typography>
                            </Box>

                            {/* Buttons */}
                            <Stack direction="row" spacing={1}>
                                <Button variant="outlined" fullWidth
                                    onClick={() => { setSelected(null); setQuery(""); resetForm(); }}
                                    sx={{
                                        textTransform: "none", fontSize: "var(--font-size-sm)", fontWeight: 500, py: 0.65,
                                        color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)",
                                        "&:hover": { borderColor: "var(--color-border-secondary)", bgcolor: "var(--color-surface-hover)" },
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button startIcon={<AddIcon sx={{ fontSize: 18 }} />} variant="contained" fullWidth
                                    onClick={handleAddPending}
                                    sx={{
                                        textTransform: "none", fontSize: "var(--font-size-sm)", fontWeight: 500, py: 0.65,
                                        bgcolor: "var(--color-primary)", boxShadow: "none",
                                        "&:hover": { bgcolor: "var(--color-primary-dark)", boxShadow: "none" },
                                    }}
                                >
                                    Add to list
                                </Button>
                            </Stack>
                        </Stack>
                    </Fade>
                )}

                {/* ── PENDING LIST ── */}
                {pending.length > 0 && (
                    <Fade in timeout={300}>
                        <Box>
                            <Divider sx={{ my: 2, borderColor: "var(--color-border-primary)" }} />
                            <Typography fontWeight={600} mb={1.25} fontSize="var(--font-size-sm)" color="var(--color-text-primary)">
                                Pending holdings ({pending.length})
                            </Typography>

                            <Stack spacing={1} mb={2}>
                                {pending.map((p, idx) => (
                                    <Box key={idx} display="flex" gap={1.25} alignItems="center"
                                        sx={{ bgcolor: "var(--color-bg-secondary)", borderRadius: 1, p: 1.25, border: "1px solid var(--color-border-primary)" }}>

                                        <Box sx={{ minWidth: 60 }}>
                                            <Typography fontWeight={600} fontSize="var(--font-size-sm)" color="var(--color-primary)">{p.ticker}</Typography>
                                            <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)">{p.purchase_date}</Typography>
                                        </Box>

                                        <Box>
                                            <TextField
                                                size="small"
                                                type="text"
                                                inputMode="numeric"
                                                label="Quantity"
                                                placeholder="e.g. 10"
                                                value={p.shares}
                                                error={!!pendingSharesErrors[idx]}
                                                onChange={(e) => updatePendingShares(idx, e.target.value)}
                                                InputProps={{
                                                    sx: {
                                                        fontSize: "var(--font-size-sm)", borderRadius: 1,
                                                        bgcolor: "var(--color-surface)",
                                                        color: "var(--color-text-primary)",
                                                        "& fieldset": { borderColor: pendingSharesErrors[idx] ? "var(--color-error)" : "var(--color-border-primary)" },
                                                        "&:hover fieldset": { borderColor: pendingSharesErrors[idx] ? "var(--color-error)" : "var(--color-border-secondary)" },
                                                        "& input::placeholder": { color: "var(--color-text-muted)", opacity: 0.3 },
                                                    },
                                                }}
                                                InputLabelProps={{ sx: { fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" } }}
                                                sx={{ width: 90 }}
                                            />
                                            {pendingSharesErrors[idx] && (
                                                <Typography fontSize="var(--font-size-xs2)" color="var(--color-error)" mt={0.4} lineHeight={1.3}>
                                                    Required
                                                </Typography>
                                            )}
                                        </Box>

                                        <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)"
                                            sx={{ minWidth: 95, display: "flex", flexDirection: "column", gap: 0.25 }}>
                                            <span style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.03em" }}>Total</span>
                                            <span style={{ fontWeight: 600, color: "var(--color-success)", fontSize: "var(--font-size-sm)" }}>
                                                {sym}{fmtNumber(p.shares * p.cost_basis, 2)}
                                            </span>
                                        </Typography>

                                        <IconButton onClick={() => removePending(idx)} size="small"
                                            sx={{ ml: "auto", color: "var(--color-text-muted)", p: 0.5, "&:hover": { bgcolor: "var(--color-surface-hover)", color: "var(--color-error)" } }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>

                            <Button fullWidth variant="contained" disabled={saving} onClick={handleSaveAll}
                                sx={{
                                    textTransform: "none", fontSize: "var(--font-size-sm)", fontWeight: 500, py: 0.85,
                                    bgcolor: "var(--color-primary)", boxShadow: "none",
                                    "&:hover": { bgcolor: "var(--color-primary-dark)", boxShadow: "none" },
                                }}
                            >
                                {saving
                                    ? <CircularProgress size={16} color="inherit" />
                                    : `Add ${pending.length} holding${pending.length > 1 ? "s" : ""} to portfolio`
                                }
                            </Button>
                        </Box>
                    </Fade>
                )}
            </DialogContent>
        </Dialog>
    );
};

const OptionRow = ({ item, onSelect, disabled }) => (
    <Box
        onClick={() => !disabled && onSelect(item)}
        sx={{
            px: 1, py: 0.85, borderRadius: 1,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.4 : 1,
            transition: "all 0.15s ease",
            border: "1px solid transparent",
            "&:hover": {
                bgcolor: disabled ? "transparent" : "var(--color-surface-hover)",
                borderColor: disabled ? "transparent" : "var(--color-border-primary)",
            },
        }}
    >
        <Typography fontWeight={600} fontSize="var(--font-size-sm)" color="var(--color-primary)">{item.ticker}</Typography>
        <Typography fontSize="var(--font-size-base)" color="var(--color-text-muted)" mt={0.25} lineHeight={1.4}>{item.company_name}</Typography>
    </Box>
);

export default AddHoldingsDialog;